/**
 * Display helpers for the Finance, Creator Center and Marketing overviews.
 *
 * Money arrives from the API as INTEGER MINOR UNITS (centavos). It is only ever
 * divided for display, here, and never added or compared as a float elsewhere.
 */

const formatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(currency: string): Intl.NumberFormat {
  let f = formatters.get(currency);
  if (!f) {
    try {
      f = new Intl.NumberFormat("en-PH", { style: "currency", currency });
    } catch {
      // An unrecognised ISO code must not blank the screen.
      f = new Intl.NumberFormat("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    formatters.set(currency, f);
  }
  return f;
}

export function formatMinor(minor: number, currency = "PHP"): string {
  const f = moneyFormatter(currency);
  const text = f.format(minor / 100);
  // The fallback formatter has no symbol; keep the code visible.
  return f.resolvedOptions().style === "currency" ? text : `${text} ${currency}`;
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat("en-PH").format(n);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDay(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

/** "3 days" / "5 hours" — how long something has been waiting. */
export function ageLabel(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((now - then) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} days`;
}

export function humanize(value: string): string {
  const s = value.replaceAll("_", " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Sum `count` over rows whose status is in the set. */
export function countWhere<T extends { status: string; count: number }>(
  rows: T[] | undefined,
  statuses: readonly string[],
): number {
  if (!rows) return 0;
  const set = new Set(statuses);
  return rows.reduce((n, r) => (set.has(r.status) ? n + r.count : n), 0);
}

/** Trigger a browser download of `text` as a file. */
export function downloadTextFile(filename: string, text: string, mime = "text/csv;charset=utf-8") {
  // The BOM makes Excel read the UTF-8 in names and references correctly.
  const blob = new Blob(["﻿", text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
